import { useRef } from 'react';
import { DatabaseBackup } from 'lucide-react';
import { SectionHeading } from '../../components/SectionHeading';
import { useSpotlight } from '../../hooks';
import { TECH_PATHS } from '../../techIcons';

interface Tech {
  /** Simple Icons slug, or undefined for a wordmark fallback. */
  slug?: string;
  name: string;
  brand: string;
}

interface Group {
  label: string;
  note: string;
  items: Tech[];
}

const GROUPS: Group[] = [
  {
    label: 'Build',
    note: 'The interface and the services behind it',
    items: [
      { slug: 'react', name: 'React', brand: '#61DAFB' },
      { slug: 'nodedotjs', name: 'Node', brand: '#5FA04E' },
      { slug: 'python', name: 'Python', brand: '#3776AB' },
      { slug: 'tailwindcss', name: 'Tailwind CSS', brand: '#06B6D4' },
    ],
  },
  {
    label: 'Data',
    note: 'Picked for the shape of your data, not habit',
    items: [
      { slug: 'postgresql', name: 'PostgreSQL', brand: '#4169E1' },
      { slug: 'mysql', name: 'MySQL', brand: '#4479A1' },
      { slug: 'mongodb', name: 'MongoDB', brand: '#47A248' },
      { slug: 'sqlite', name: 'SQLite', brand: '#0B4F6C' },
    ],
  },
  {
    label: 'Deploy',
    note: 'Your cloud account or ours — five platforms in production',
    items: [
      { name: 'AWS', brand: '#FF9900' },
      { slug: 'digitalocean', name: 'DigitalOcean', brand: '#0080FF' },
      { slug: 'railway', name: 'Railway', brand: '#7C5CFF' },
      { slug: 'hetzner', name: 'Hetzner', brand: '#D50C2D' },
      { slug: 'pythonanywhere', name: 'PythonAnywhere', brand: '#1D9FD7' },
    ],
  },
];

function TechMark({ tech }: { tech: Tech }): React.JSX.Element {
  const path = tech.slug ? TECH_PATHS[tech.slug] : undefined;
  if (!path) {
    // AWS and anything else without a licensed mark: styled wordmark.
    return <span className="tech-wordmark">{tech.name}</span>;
  }
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden="true" focusable="false">
      <path d={path} fill="currentColor" />
    </svg>
  );
}

/** Seconds per loop, varied so the rows never march in lockstep. */
const SPEEDS = [32, 26, 38];

/**
 * One run of a group's logos, repeated enough times to overflow the
 * container — the track holds two identical runs and slides -50%, so
 * the loop is seamless.
 */
function TechRun({ items, duplicate }: { items: Tech[]; duplicate?: boolean }): React.JSX.Element {
  const run = [...items, ...items, ...items];
  return (
    <ul className="tech-run" aria-hidden={duplicate || undefined}>
      {run.map((tech, i) => (
        <li
          key={`${tech.name}-${i}`}
          className="tech-pill"
          style={{ ['--brand' as string]: tech.brand }}
        >
          <span className="tech-logo">
            <TechMark tech={tech} />
          </span>
          <span className="tech-name">{tech.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function TechStack(): React.JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  useSpotlight(gridRef);

  return (
    <section id="stack" className="tech">
      <div className="container">
        <SectionHeading
          anim="blur"
          title={
            <>
              The stack, <span className="si accent-text">end to end</span>.
            </>
          }
          lede="No mystery layers. Everything your system is built on and everything it runs on — the same set we support after launch."
        />

        <div className="tech-strips" ref={gridRef}>
          {GROUPS.map((group, gi) => (
            <div key={group.label} className="tech-strip-row reveal" data-delay={gi + 1}>
              <div className="tech-strip-head">
                <h3>{group.label}</h3>
                <p>{group.note}</p>
              </div>

              <div className="tech-strip">
                <div
                  className={`tech-strip-track ${gi % 2 === 1 ? 'reverse' : ''}`}
                  style={{ ['--speed' as string]: `${SPEEDS[gi] ?? 34}s` }}
                >
                  <TechRun items={group.items} />
                  <TechRun items={group.items} duplicate />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="stack-backup reveal">
          <span className="stack-backup-icon" aria-hidden="true">
            <DatabaseBackup size={22} strokeWidth={1.8} />
          </span>
          <div>
            <h3 className="h3">Automated database backups</h3>
            <p>
              Where a client needs them, we set backups to run on a schedule rather than
              leaving them to somebody remembering. It means there is always a recent
              restore point, and recovering is a decision instead of a scramble.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
